// src/layouts/UserLayout.jsx
import React, { useState, useContext, useEffect, useMemo } from 'react';
import TopBar from '../components/global/TopBar';
import StepsBottomBar from '../components/global/StepsBottomBar';
import { Outlet, useLocation } from 'react-router-dom';
import { UserProfileContext } from '../contexts/UserProfileContext';
import UserProfileModal from '../components/modals/UserProfileModal';
import ErrorView from '../components/errors/ErrorView';
import { handleSmartBack } from '../../shared/utils/navigationUtils';
import { seedProfileFromAdmin } from '../utils/seedProfileFromAdmin';
import { isAdminWorkflowPreviewPath } from '../utils/adminWorkflowPreviewPath';

const ACCESS_KEY_PATH_REGEX = /\/assets\/access_key\/([^/]+)/;

export default function UserLayout() {
  const {
    userProfile,
    setShowProfileModal,
    isProfileComplete,
    hasValidAccessKey,
    isUserWorkflowAccess,
    updateUserProfile,
  } = useContext(UserProfileContext);

  const location = useLocation();
  const isAdminWorkflowPreview = isAdminWorkflowPreviewPath(location.pathname);

  // The currently routed access key — User Workflow access keys must never
  // see the profile modal so identity stays optional/anonymous.
  const currentAccessKey = useMemo(() => {
    const match = location.pathname.match(ACCESS_KEY_PATH_REGEX);
    return match ? match[1] : null;
  }, [location.pathname]);

  const suppressProfileModal =
    isAdminWorkflowPreview ||
    (!!currentAccessKey && isUserWorkflowAccess(currentAccessKey));


  // Default top bar properties
  const [topBarProps, setTopBarProps] = useState({
    headerText: '',
    onLeftIconClick: handleSmartBack,
    rightIconClass: '',
    onRightIconClick: () => {},
  });

  useEffect(() => {
    seedProfileFromAdmin(userProfile, updateUserProfile);
  }, [userProfile, updateUserProfile]);

  // Check if the modal should be shown
  useEffect(() => {
    if (suppressProfileModal) {
      setShowProfileModal(false);
      return;
    }
    if (!isProfileComplete()) {
      setShowProfileModal(true);
    }
  }, [userProfile, setShowProfileModal, isProfileComplete, suppressProfileModal]);

  // Field-engineer routes need an asset access key; admin workflow preview does not.
  if (!hasValidAccessKey() && !isAdminWorkflowPreview) {
    return (
      <div className="narrow-view">
        <TopBar {...topBarProps} />
        <ErrorView 
          title="Access Required"
          message="You need a valid access key to use this application. Please check your link or contact your administrator."
          isFirstTimeUser={true}
        />
      </div>
    );
  }

  return (
    <div className="narrow-view">
      <TopBar {...topBarProps} />
      <Outlet context={{ setTopBarProps }} />
      <StepsBottomBar />
      {!suppressProfileModal && <UserProfileModal />}
    </div>
  );
}